import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistration } from '../store/RegistrationContext';
import { registrationActionTypes } from '../store/registrationReducer';
import { retreatFlowSteps } from '../domain/retreatFlowSteps';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StepperHeader from '../components/ui/StepperHeader';

const SessionSelectionPage = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useRegistration();
  
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!state.selectedRetreatId) {
      navigate('/retreat');
      return;
    }

    if (!state.selectedStudentIds || state.selectedStudentIds.length === 0) {
      navigate('/students');
      return;
    }

    // Generate date range sessions from retreat start to end date
    generateSessions();
  }, [state.selectedRetreatId, state.selectedStudentIds, navigate]);

  const generateSessions = () => {
    // Mock retreat dates for now - in real implementation, fetch from API
    const startDate = new Date('2026-02-14');
    const endDate = new Date('2026-02-17');
    
    const sessionList = [];
    let currentDate = new Date(startDate);
    let sessionId = 1;

    while (currentDate <= endDate) {
      sessionList.push({
        id: sessionId++,
        date: new Date(currentDate),
        dateString: currentDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        selected: false
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setSessions(sessionList);
  };

  const toggleSessionSelection = (sessionId) => {
    setSessions(sessions.map(session => 
      session.id === sessionId 
        ? { ...session, selected: !session.selected }
        : session
    ));
  };

  const handleContinue = () => {
    const selectedSessions = sessions.filter(s => s.selected);
    
    if (selectedSessions.length === 0) {
      alert('Please select at least one session date');
      return;
    }

    // Store selected sessions in state
    dispatch({
      type: registrationActionTypes.SET_SELECTED_SESSIONS,
      payload: selectedSessions
    });

    navigate('/review');
  };

  const handleBack = () => {
    navigate('/students');
  };

  const selectedCount = sessions.filter(s => s.selected).length;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-32 pt-4">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <StepperHeader
            title="Select Session Dates"
            subtitle="Choose the dates you would like to attend"
            steps={retreatFlowSteps}
            activeIndex={2}
            onBack={handleBack}
          />

          <div className="mt-6 bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
            {/* Info Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-calendar-alt text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">Select Your Session Dates</h3>
                  <p className="text-sm text-blue-50">
                    Choose the dates you would like to attend during the retreat period. 
                    You can select multiple dates or the entire duration.
                  </p>
                </div>
              </div>
            </div>

            {/* Retreat Info */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-600">Retreat Period</h4>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    February 14, 2026 - February 17, 2026
                  </p>
                </div>
                <div className="text-right">
                  <h4 className="text-sm font-semibold text-gray-600">Selected Dates</h4>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{selectedCount}</p>
                </div>
              </div>
            </div>

            {/* Session Selection */}
            <div className="p-6">
              {sessions.length === 0 ? (
                <div className="text-center py-12">
                  <i className="fas fa-calendar-times text-5xl text-gray-300 mb-4"></i>
                  <p className="text-gray-600">No sessions available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => toggleSessionSelection(session.id)}
                      className={`
                        relative p-5 rounded-2xl border-2 cursor-pointer transition-all duration-300
                        ${session.selected 
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg scale-105' 
                          : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                        }
                      `}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`
                          flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center
                          ${session.selected 
                            ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' 
                            : 'bg-gray-100 text-gray-600'
                          }
                        `}>
                          <i className="fas fa-calendar-day text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${session.selected ? 'text-purple-700' : 'text-gray-600'}`}>
                            {session.date.toLocaleDateString('en-US', { weekday: 'long' })}
                          </p>
                          <p className={`text-lg font-bold ${session.selected ? 'text-purple-900' : 'text-gray-900'}`}>
                            {session.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        {session.selected && (
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                              <i className="fas fa-check text-white text-sm"></i>
                            </div>
                          </div>
                        )}
                      </div>
                      {session.selected && (
                        <div className="absolute top-3 right-3">
                          <span className="inline-block px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                            Selected
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Quick Select Options */}
              {sessions.length > 0 && (
                <div className="mt-6 flex gap-3 justify-center">
                  <button
                    onClick={() => setSessions(sessions.map(s => ({ ...s, selected: true })))}
                    className="px-6 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-colors"
                  >
                    Select All Dates
                  </button>
                  <button
                    onClick={() => setSessions(sessions.map(s => ({ ...s, selected: false })))}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
      </div>

      <Footer
        onBack={handleBack}
        onContinue={handleContinue}
        continueText="Continue to Review"
        continueDisabled={selectedCount === 0}
      />
    </>
  );
};

export default SessionSelectionPage;
